import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import type { DicomQueryContext } from "@/types/dicomweb";
import { resolveParamToHexTag } from "@/utils/dicom";
import UploadPage from "../pages/upload/UploadPage.tsx";

/**
 * Index route definition
 *
 * This file defines the root route ("/") of the application using
 * `@tanstack/react-router`. The root route renders the `UploadPage` component.
 */

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>): IndexRouteSearch => {
    return {
      studies: search.studies
        ?.toString()
        .split(",")
        .filter((s) => s.trim() !== ""),
    };
  },
  component: IndexRouteComponent,
});

type IndexRouteSearch = {
  studies?: string[];
  tags?: DicomQueryContext;
};

function IndexRouteComponent() {
  const search = Route.useSearch();
  const { studies } = search;

  // Reconstruct dicom tags from raw search parameters so we don't store
  // a nested `tags` object in the router search (which would be serialized
  // as "[object Object]" in the URL). Accept tag keys, dcmjs keywords and
  // configured keys via `resolveParamToHexTag`.
  const tags: DicomQueryContext = {};
  for (const [key, value] of Object.entries(
    search as Record<string, unknown>,
  )) {
    if (key === "studies") continue;
    const hexTag = resolveParamToHexTag(key);
    // TanStack's qss decoder coerces purely-numeric search params to numbers
    // (e.g. PatientBirthDate "20051212" -> number 20051212) before our parser
    // runs, so stringify the value rather than assuming it's already a string.
    const str = String(value ?? "").trim();
    if (hexTag && str !== "") {
      tags[hexTag] = str;
    }
  }
  const navigate = Route.useNavigate();
  const clearStudiesSearch = useCallback(() => {
    navigate({
      search: (previousSearch) => ({
        ...previousSearch,
        studies: undefined,
        tags: undefined,
      }),
      replace: true,
      ignoreBlocker: true,
    });
  }, [navigate]);

  return (
    <UploadPage
      studies={studies}
      dicomTags={Object.keys(tags).length > 0 ? tags : undefined}
      onCancelUploadWorkflow={clearStudiesSearch}
    />
  );
}
